<script setup lang="ts">
import { useCurrentUser } from "@/api/current-user";
import { useRoleLabel } from "@/composable/role-label";
import { Action, resolvePermissions, Resource } from "@/stores/permission";
import { Role, ScopeType } from "@soft-db/monitoring-api-client";
import {
	Badge,
	DataTableContent,
	DataTableRoot,
	DataTableSearch,
	Dialog,
	makeColumnHelper,
} from "@soft-db/ui";
import ScopeGuard from "../permission/ScopeGuard.vue";
import PermissionBadge from "./PermissionBadge.vue";

const props = defineProps<{
	scope: ScopeType;
}>();

const open = defineModel<boolean>("open");

const baseAction: Action[] = ["create", "get", "update", "delete"];

const subscribeAction: Action = "subscribe";

const resourceWithSubscription: Resource[] = ["station", "site", "alert", "report"];

const resources = computed<Resource[]>(() => {
	const resources: Resource[] = [
		"station",
		"site",
		"alert",
		"report",
		"space",
		"user",
		"public-page",
		"dashboard",
		"dashboard.own",
	];

	switch (props.scope) {
		case "distributor":
			return resources;
		case "space":
			return resources.filter((r) => r !== "space");
		case "none":
			return [];
	}
});

const { currentUser, hasRole } = useCurrentUser();

const roles = computed(() => {
	let allRoles: Role[] = ["admin", "editor", "viewer"];

	return allRoles.filter((r) => hasRole(r));
});

function resolveActions(resource: Resource) {
	const actions = [...baseAction];
	if (resourceWithSubscription.includes(resource)) {
		actions.push(subscribeAction);
	}

	return actions;
}

function hasPermission(role: Role, resource: Resource, action: Action) {
	const permissionSet = resolvePermissions(props.scope, role);

	return permissionSet.has(`${resource}.${action}`);
}

const { t } = useI18n();
function resourceLabel(resource: Resource): string {
	switch (resource) {
		case "station":
			return t("object.station");

		case "site":
			return t("object.site");

		case "alert":
			return t("object.alert");

		case "report":
			return t("object.report");

		case "space":
			return t("object.space");

		// case "distributor":
		// 	return t("object.distributor");
		case "distributor":
			return t("object.distributor");
		case "user":
			return t("object.user");

		case "public-page":
			return t("object.publicPage");
		case "dashboard":
			return `${t("common.dashboard")} (${t("dashboard.layoutDefault")})`;
		case "dashboard.own":
			return `${t("common.dashboard")} (${t("dashboard.layoutUser")})`;
	}

	return "";
}

function scopeLabel(scope: ScopeType): string {
	switch (scope) {
		case "distributor":
			return t("object.distributor");
		case "space":
			return t("object.space");
		case "none":
			return "";
	}
}

type PermissionRow = {
	resource: Resource;
	resourceLabel: string;
	actions: Action[];
};

const rows = computed<PermissionRow[]>(() => {
	if (currentUser.value == null) {
		return [];
	}

	const rows: PermissionRow[] = [];
	for (const resource of resources.value) {
		if (!hasPermission(currentUser.value.role, resource, "get")) {
			continue;
		}

		rows.push({
			resource,
			resourceLabel: resourceLabel(resource),
			actions: resolveActions(resource),
		});
	}

	return rows;
});

const { Column } = makeColumnHelper<PermissionRow>();

const roleLabel = useRoleLabel();
</script>

<template>
	<Dialog v-model:open="open" class="w-300" closeButton>
		<DataTableRoot :data="rows">
			<div class="mt-4 flex flex-col gap-2">
				<div class="flex items-end justify-between">
					<DataTableSearch />
					<ScopeGuard scope="distributor">
						<Badge :color="scope === 'distributor' ? 'primary' : 'warning'">
							{{ scopeLabel(scope) }}
						</Badge>
					</ScopeGuard>
				</div>
				<DataTableContent class="no-hover table-vertical-separator">
					<Column
						field="resourceLabel"
						:header="`${t('common.entity')} / ${t('common.role')}`"
						enableGlobalFilter
					/>
					<Column v-for="role of roles" :key="role" :header="roleLabel(role)">
						<template #header>
							<div class="text-center">{{ roleLabel(role) }}</div>
						</template>
						<template #default="{ row }">
							<div class="grid grid-cols-2 gap-2">
								<div v-for="action of row.original.actions" :key="action">
									<PermissionBadge
										:action="action"
										:hasAccess="hasPermission(role, row.original.resource, action)"
									/>
								</div>
							</div>
						</template>
					</Column>
				</DataTableContent>
			</div>
		</DataTableRoot>
	</Dialog>
</template>
